import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { FormCreator } from "@/components/shared/form-creator";
import { toast } from "sonner";
import { useWizard } from "@/app/auth/signup/signup.components/wizard/wizard.hooks.ts";
import { Link } from "react-router";

const schema = z.object({ email: z.string().email().nonempty() });

type EmailFormSchema = z.infer<typeof schema>;

export function EmailStep() {
  const { submitEmail, loading, error } = useWizard();

  const form = useForm<EmailFormSchema>({
    resolver: zodResolver(schema),
    defaultValues: {
      email: "",
    },
  });

  const submit = async ({ email }: { email: string }) => {
    try {
      await submitEmail(email);
    } catch (err: any) {
      toast.error(err.message ?? "Error");
    }
  };

  return (
    <>
      <div className="flex flex-col items-start gap-2 text-center mb-6">
        <h1 className="text-2xl font-bold">Create your account</h1>
        <p className="text-balance text-sm text-muted-foreground">Enter your email to get started</p>
      </div>

      <FormCreator
        form={form}
        formDefinition={[{ type: "email", name: "email", label: "Email" }]}
        buttonText={loading ? "Checking…" : "Next"}
        onSubmit={submit}
      />

      {error && <p className="text-sm text-destructive text-center mt-4">{error}</p>}

      <p className="text-xs text-muted-foreground text-center leading-5 mt-6">
        By creating your account, you agree to our{" "}
        <Link to="/terms" className="underline underline-offset-4">
          Terms of Use
        </Link>{" "}
        &{" "}
        <Link to="/privacy" className="underline underline-offset-4">
          Privacy Policy
        </Link>
      </p>

      <div className="text-center text-sm mt-2">
        Already have an account?{" "}
        <Link to="/login" className="underline underline-offset-4">
          Log in
        </Link>
      </div>
    </>
  );
}
